import React, { Component, SyntheticEvent } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { replace } from 'redux-first-history';
import { parse } from 'query-string';

import styled from 'styled-components';
import { Box } from '@material-ui/core';

import dayjs from '@api/services/tools/dayjs';
import { isEmpty, isEqual, sortBy, forEach, flatten } from 'lodash-es';

import {
    Typography,
    ElasticTheme as Theme,
    CampaignIcon,
    DateDropdown,
    SimplePopover,
    SimpleLoader,
    FiltersPopover,
    FilterIcon,
    LineChart,
    palette,
    formatNumber,
    FilterButton,
    ContentLoader,
} from '@dev/ee-components';
import { convertToLocalTime, safelyDecodeBase64Url } from '@dev/ee-tools';

import { statusTypes } from '../../../StatisticsInterface';

import OpenedIcon from '../../../icons/opened.svg';
import ClickedIcon from '../../../icons/clicked.svg';
import BouncedIcon from '../../../icons/bounced.svg';

import { ApplicationState as State } from '@api/store';
import { LoadSummaryStart, LoadSummaryStartParams } from '@api/store/statistics/actions';
import { LoadSingleChannelStart } from '@api/store/channels/actions';
import SingleChannelProps from '../SingleChannelInterface';
import { addParamToUrl, parseUrlParams, setAtEndOfDay } from '@api/services/tools/handleUrlParams';
import { SingleChannelOverviewState } from './SingleChannelOverviewInterface';
import { withRouter } from 'react-router-dom';
import { getDateDropdownValue } from '@api/services/tools/getDateDropdownValue';
import { DateDropdownOptions } from '@api/services/dictionaries/DateRangeValues';

const mapState = (state: State, ownProps: SingleChannelProps): SingleChannelProps => {
    return {
        ...ownProps,
        browser: state.browser,
        channels: state.channels,
        statistics: state.statistics,
    };
};

const dispatchProps = {
    LoadSummaryStart,
    LoadSingleChannelStart,
    Replace: replace,
};

type DispatchProps = {
    LoadSummaryStart(params: LoadSummaryStartParams);
    LoadSingleChannelStart(name: string);
    Replace(param: Record<string, string> | string): void;
};

type Props = DispatchProps & SingleChannelProps;

const TopPanels = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const Panel = styled.div<{ isMobile: boolean }>`
    padding: 24px 32px;
    width: ${(props): string => (props.isMobile ? '100%' : 'auto')};
    flex: ${(props): string => (props.isMobile ? '' : '1 1 20%')};
    margin-top: 8px;
    margin-right: ${(props): string => (props.isMobile ? '0px' : '30px')};
    margin-bottom: ${(props): string => (props.isMobile ? '32px' : '0px')};
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};

    &:last-of-type {
        margin-right: 0px;
        margin-bottom: 0px;
    }
`;

const IconContainer = styled.div`
    margin-left: 8px;
    display: inline-block;
`;

const GraphContent = styled.div`
    width: 100%;
    margin-top: 3rem;
    padding: 24px 32px;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
`;

const GraphHeader = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const MobileGraphHeader = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 96px;
    width: 100%;
`;

const defaultCheckboxValue: statusTypes[] = [
    'Submitted',
    'Delivered',
    'Opened',
    'Clicked',
    'Unsubscribed',
    'Bounced',
    'Complaints',
    'Suppressed',
];

const EMPTY_GRAPH_HEIGHT = 400;
const FILLED_GRAPH_HEIGHT = 900;
const DATE_FORMAT = 'YYYY-MM-DD';

const DEFAULT_DATE_RANGE_VALUE = '30';

class SingleChannelOverview extends Component<Props, SingleChannelOverviewState> {
    public state = {
        from: null,
        to: null,
        statuses: defaultCheckboxValue,
        currentCheckedStatuses: [],
        dateFilterValue: DEFAULT_DATE_RANGE_VALUE,
    };

    public componentDidMount(): void {
        window.scrollTo(0, 0);
        const { LoadSummaryStart, LoadSingleChannelStart, match } = this.props;
        const query = parseUrlParams(location.search);
        const params = {
            channelName: safelyDecodeBase64Url(match.params.channelName),
            from: this.state.from || dayjs().subtract(30, 'day').startOf('day').utc().format(),
            to: this.state.to || dayjs().endOf('day').utc().format(),
        };

        let selectedOption: string = DEFAULT_DATE_RANGE_VALUE;
        let queryStatuses: statusTypes[] = null;

        if (!isEmpty(query)) {
            if (query.statuses) {
                queryStatuses = flatten([query.statuses]) as statusTypes[];
            }

            if (query?.from && query?.to) {
                const from = dayjs.utc(query?.from as string);
                const to = dayjs.utc(query?.to as string);
                let dateDiff = to.diff(from, 'day');

                if (dateDiff > 31) {
                    dateDiff = to.diff(from, 'month');
                }
                const dateOptions = this.getDateDropdownOptions();

                selectedOption = dateOptions?.find((el) => +el.value === +dateDiff)?.value || 'calendar';

                params.from = from.toJSON();
                params.to = setAtEndOfDay(to.toJSON());
            }
        }
        this.setState({
            from: params.from,
            to: params.to,
            statuses: queryStatuses || defaultCheckboxValue,
            currentCheckedStatuses: queryStatuses?.length === defaultCheckboxValue.length ? [] : queryStatuses,
            dateFilterValue: selectedOption,
        });

        LoadSummaryStart(params);
        LoadSingleChannelStart(params.channelName);
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private renderTopPanels(): JSX.Element {
        const { t, browser } = this.props;
        const { singleChannel, singleChannelLoading } = this.props.channels;
        const isMobile = browser.lessThan.sm;

        return (
            <TopPanels>
                <Panel isMobile={isMobile}>
                    {singleChannelLoading ? (
                        <ContentLoader mode={'row'} height={20} count={2} />
                    ) : (
                        <>
                            <Typography variant={'h1'}>
                                {formatNumber(singleChannel?.RecipientCount) || 0}
                                <IconContainer>
                                    <CampaignIcon />
                                </IconContainer>
                            </Typography>
                            <Typography variant={'subtitle1'} display="inline">
                                <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                                    {t('submitted')}
                                </Box>
                            </Typography>
                        </>
                    )}
                </Panel>
                <Panel isMobile={isMobile}>
                    {singleChannelLoading ? (
                        <ContentLoader mode={'row'} height={20} count={2} />
                    ) : (
                        <>
                            <Typography variant={'h1'}>
                                {formatNumber(singleChannel?.OpenedCount) || 0}
                                <IconContainer>
                                    <OpenedIcon />
                                </IconContainer>
                            </Typography>
                            <Typography variant={'subtitle1'} display="inline">
                                <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                                    {t('opened')}
                                </Box>
                            </Typography>
                        </>
                    )}
                </Panel>
                <Panel isMobile={isMobile}>
                    {singleChannelLoading ? (
                        <ContentLoader mode={'row'} height={20} count={2} />
                    ) : (
                        <>
                            <Typography variant={'h1'}>
                                {formatNumber(singleChannel?.ClickedCount) || 0}
                                <IconContainer>
                                    <ClickedIcon />
                                </IconContainer>
                            </Typography>
                            <Typography variant={'subtitle1'} display="inline">
                                <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                                    {t('clicked')}
                                </Box>
                            </Typography>
                        </>
                    )}
                </Panel>
                <Panel isMobile={isMobile}>
                    {singleChannelLoading ? (
                        <ContentLoader mode={'row'} height={20} count={2} />
                    ) : (
                        <>
                            <Typography variant={'h1'}>
                                {formatNumber(singleChannel?.FailedCount) || 0}
                                <IconContainer>
                                    <BouncedIcon />
                                </IconContainer>
                            </Typography>
                            <Typography variant={'subtitle1'} display="inline">
                                <Box color={Theme.typography.h1.color} fontWeight={Theme.typography.h1.fontWeight}>
                                    {t('bounced')}
                                </Box>
                            </Typography>
                        </>
                    )}
                </Panel>
            </TopPanels>
        );
    }

    private getDateDropdownOptions(mobile?: boolean) {
        const { t } = this.props;

        const calendarOption = {
            label: t('specificdaterange'),
            value: 'calendar',
            borderBottom: true,
        };

        const options = [
            {
                label: t('last7days'),
                value: '7',
            },
            {
                label: t('last14days'),
                value: '14',
            },
            {
                label: t('last30days'),
                value: '30',
            },
            {
                label: t('last3months'),
                value: '90',
            },
            {
                label: t('last6months'),
                value: '180',
            },
            {
                label: t('lastyear'),
                value: '365',
            },
        ];
        if (mobile) {
            return [...options, calendarOption];
        }
        return [calendarOption, ...options];
    }

    private renderDateRange = (): JSX.Element => {
        const { LoadSummaryStart, match } = this.props;
        const channelName = safelyDecodeBase64Url(match.params.channelName);
        const dateFilterValue = getDateDropdownValue(true, DateDropdownOptions.Last30days);
        const query = parse(location.search);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();

        return (
            <div style={{ marginRight: '8px' }}>
                <DateDropdown
                    onItemClick={(e, obj): void => {
                        const dateValue = obj.value;
                        const dateValueNumeric = parseInt(`${obj.value}`, 10);
                        const to = dayjs.utc();
                        let from = null;

                        if ((dateValue as string) !== 'calendar') {
                            switch (dateValue) {
                                case '3':
                                case '6':
                                    from = dayjs().subtract(dateValueNumeric, 'month').startOf('day');
                                    break;
                                case '12':
                                    from = dayjs().subtract(1, 'year').startOf('day');
                                    break;
                                default:
                                    from = dayjs().subtract(dateValueNumeric, 'd').startOf('day');
                            }

                            this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
                            this.setState({ from: from.format(), to: to.format(), dateFilterValue: dateValue }, (): void => {
                                LoadSummaryStart({ from: from.format(), to: to.format(), channelName });
                            });
                        }
                    }}
                    type="input"
                    maxHeight={500}
                    value={dateFilterValue}
                    returnRawData
                    options={this.getDateDropdownOptions()}
                    calendarOnChange={(fromTime: Date, toTime: Date): void => {
                        const from = dayjs(fromTime).add(1, 'day').startOf('day').utc();
                        const to = dayjs(toTime).endOf('day').utc();

                        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
                        this.setState({ from: from.toJSON(), to: to.toJSON() }, (): void => {
                            LoadSummaryStart({ from: from.toJSON(), to: to.toJSON(), channelName });
                        });
                    }}
                    buttonVariant="tertiary"
                    placement={'bottom-end'}
                    startDate={startDate}
                    endDate={endDate}
                />
            </div>
        );
    };

    private getFilterOptions() {
        const { t } = this.props;
        return [
            {
                label: t('submitted'),
                value: 'Submitted',
            },
            {
                label: t('delivered'),
                value: 'Delivered',
            },
            {
                label: t('opened'),
                value: 'Opened',
            },
            {
                label: t('clicked'),
                value: 'Clicked',
            },
            {
                label: t('unsubscribed'),
                value: 'Unsubscribed',
            },
            {
                label: t('bounced'),
                value: 'Bounced',
            },
            {
                label: t('complaints'),
                value: 'Complaints',
            },
            {
                label: t('suppressed'),
                value: 'Suppressed',
            },
        ];
    }

    private handleFiltersCheckboxClick(event: SyntheticEvent<HTMLInputElement>, statuses): void {
        const newStatuses = !statuses.length ? defaultCheckboxValue : statuses;

        this.addSearchQuery({ statuses: newStatuses });

        this.setState({
            statuses: newStatuses,
            currentCheckedStatuses: statuses,
        });
    }

    private renderFilterSelect = (): JSX.Element => {
        const { currentCheckedStatuses } = this.state;
        const { t } = this.props;
        const filters = this.getFilterOptions();

        return (
            <SimplePopover
                type="noborder"
                listItemWidth="150px"
                options={filters}
                enableCheckbox={true}
                checkboxValue={currentCheckedStatuses}
                onCheckboxClick={this.handleFiltersCheckboxClick.bind(this)}
                placement="bottom-end"
                defaultPlaceholder={t('filters')}
                maxHeight={400}
            />
        );
    };

    private onLegendItemClick = (statuses): void => {
        const currentCheckedStatuses = statuses.length === defaultCheckboxValue.length ? [] : statuses;
        this.addSearchQuery({ statuses });
        this.setState({ statuses, currentCheckedStatuses });
    };

    private renderChart = (): JSX.Element => {
        const { summaryLoading, summary } = this.props.statistics;

        if (summaryLoading || isEmpty(summary)) {
            return (
                <LoadingWrapper>
                    <SimpleLoader />
                </LoadingWrapper>
            );
        }

        const { statuses } = this.state;
        const { browser } = this.props;
        const isMobile = browser.lessThan.sm;
        const chartData = this.prepareChartsData();
        const percentageData = this.preparePercentageData();
        const hasAnyGraphData = this.isAnyGraphData(chartData);

        const chartLineConfig = [
            { color: palette.blue[3], dataKey: 'Submitted' },
            { color: palette.indigo[6], dataKey: 'Delivered' },
            { color: palette.teal[6], dataKey: 'Opened' },
            { color: palette.teal[4], dataKey: 'Clicked' },
            { color: palette.purple[6], dataKey: 'Unsubscribed' },
            { color: palette.red[4], dataKey: 'Bounced' },
            { color: palette.gray[6], dataKey: 'Complaints' },
            { color: palette.gray[4], dataKey: 'Suppressed' },
        ];
        const needExpandedYAxis = chartData?.some((singleDay) => singleDay.Submitted / 1000 > 1);
        const mobileHeight = !hasAnyGraphData ? EMPTY_GRAPH_HEIGHT : FILLED_GRAPH_HEIGHT;

        return (
            <LineChart
                activeLinesFromLegend={statuses}
                lineConfig={chartLineConfig}
                data={chartData}
                isEmpty={!hasAnyGraphData}
                margin={{ left: -16 }}
                percentageData={percentageData}
                height={isMobile ? mobileHeight : 490}
                xAxisGroupName="Date"
                yAllowDecimals={false}
                showLegend={true}
                isMobile={isMobile}
                padding={{ left: 3, right: 5 }}
                fullWidth={true}
                onLegendsItemClick={this.onLegendItemClick.bind(this)}
                YAxisProps={{
                    width: needExpandedYAxis ? 65 : 45,
                }}
            />
        );
    };

    private prepareChartsData = (): Record<string, any>[] => {
        const dailyData = this.props.statistics?.summary?.DailyLogStatusSummary;

        if (!dailyData) {
            return [];
        }

        const filteredData = dailyData.filter((el) => !!el);

        return filteredData?.map((el) => ({
            Date: convertToLocalTime(el.Date, 'L'),
            Submitted: el.Email,
            Bounced: el.Bounced,
            Clicked: el.Clicked,
            Complaints: el.Complaint,
            Delivered: el.Delivered,
            Opened: el.Opened,
            Unsubscribed: el.Unsubscribed,
            Suppressed: el.Suppressed,
        }));
    };

    private preparePercentageData = (): { [key: string]: string | number } => {
        const logData = this.props.statistics?.summary?.LogStatusSummary;

        return {
            Submitted: logData?.EmailTotal === 0 ? `0%` : `100%`,
            Bounced: `${logData?.FailedPercentage}%`,
            Clicked: `${logData?.ClickedPercentage}%`,
            Complaints: `${logData?.FailedAbusePercentage}%`,
            Delivered: `${logData?.DeliveredPercentage}%`,
            Opened: `${logData?.OpenedPercentage}%`,
            Unsubscribed: `${logData?.UnsubscribedPercentage}%`,
            Suppressed: `${logData?.SuppressedPercentage}%`,
        };
    };

    private isAnyGraphData = (data): number => {
        let dataValues = 0;

        forEach(data, (el) => {
            for (const property in el) {
                if (property !== 'Date') {
                    dataValues += el[property];
                }
            }
        });
        return dataValues;
    };

    private renderMobileGraphHeader = (): JSX.Element => {
        const { t } = this.props;
        const { currentCheckedStatuses } = this.state;
        const query = parse(location.search);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();

        return (
            <MobileGraphHeader>
                <FiltersPopover
                    onApply={(obj, from, to): void => {
                        this.mobileFiltersApply(obj, from, to);
                    }}
                    tabs={[
                        {
                            label: t('daterange'),
                            defaultValue: getDateDropdownValue(true),
                            options: this.getDateDropdownOptions(true),
                            type: 'date',
                            startDate,
                            endDate,
                        },
                        {
                            defaultCheckboxValue: currentCheckedStatuses,
                            label: t('filters'),
                            options: this.getFilterOptions(),
                            type: 'checkbox',
                        },
                    ]}
                    title={t('filters')}
                    translations={{
                        apply: t('apply'),
                        close: t('close'),
                        to: t('to'),
                    }}
                >
                    <FilterButton startIcon={<FilterIcon color={palette.gray[6]} />} />
                </FiltersPopover>
            </MobileGraphHeader>
        );
    };

    private isSelectedStatusesEmpty(statuses: string[]): boolean {
        return (statuses?.length === 1 && !statuses?.[0]) || isEmpty(statuses);
    }

    private mobileFiltersApply = (filtersObj, newFrom, newTo): void => {
        const { LoadSummaryStart, match } = this.props;
        const dateValue = filtersObj[0];
        const dateValueNumeric = parseInt(`${dateValue}`, 10);
        const selectedStatuses = filtersObj[1];
        const areNewStatusesEmpty = this.isSelectedStatusesEmpty(selectedStatuses);
        const newStatuses = areNewStatusesEmpty ? defaultCheckboxValue : selectedStatuses;
        //check dateRange tab
        let to = dayjs.utc();
        let from = null;
        if (dateValue !== 'calendar') {
            switch (dateValue) {
                case '3':
                case '6':
                    from = dayjs().subtract(dateValueNumeric, 'month').startOf('day');
                    break;
                case '12':
                    from = dayjs().subtract(1, 'year').startOf('day');
                    break;
                default:
                    from = dayjs().subtract(dateValueNumeric, 'd').startOf('day');
            }
        } else {
            from = newFrom ? dayjs(newFrom).startOf('day') : dayjs().startOf('day');
            to = newTo ? dayjs(newTo).endOf('day') : dayjs().endOf('day');
        }

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
        this.setState({ from: from.utc().toJSON(), to: to.utc().toJSON(), dateFilterValue: dateValue }, (): void => {
            LoadSummaryStart({
                from: from.utc().toJSON(),
                to: to.utc().toJSON(),
                channelName: safelyDecodeBase64Url(match.params.channelName),
            });
        });

        //check filters tab
        if (!isEqual(sortBy(this.state.statuses), sortBy(newStatuses))) {
            this.addSearchQuery({ statuses: newStatuses });
            this.setState({
                statuses: newStatuses,
                currentCheckedStatuses: selectedStatuses,
            });
        }
    };

    public render(): JSX.Element {
        const { browser } = this.props;
        const isMobile = browser.lessThan.sm;
        return (
            <>
                {this.renderTopPanels()}
                <GraphContent>
                    {isMobile ? (
                        this.renderMobileGraphHeader()
                    ) : (
                        <GraphHeader>
                            {this.renderDateRange()}
                            {this.renderFilterSelect()}
                        </GraphHeader>
                    )}
                    <div style={{ position: 'relative' }}>{this.renderChart()}</div>
                </GraphContent>
            </>
        );
    }
}

export default compose(
    withTranslation(),
    withRouter,
    connect<SingleChannelProps, DispatchProps>(mapState, dispatchProps)
)(SingleChannelOverview);
